import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, KeyRound, LayoutGrid, LogOut, Monitor, Moon, Sun, Volume2, VolumeX } from 'lucide-react';
import { DropdownContent, DropdownItem, DropdownLabel, DropdownMenu, DropdownSeparator, DropdownTrigger } from '@/components/ui/dropdown';
import { logout } from '@/lib/auth';
import { useExperience, type MotionChoice, type ThemeChoice } from '@/lib/experience';
import { sound } from '@/lib/sound';
import { useSession } from '@/lib/session';
import { cn, initials } from '@/lib/utils';

export function UserMenu({ dark }: { dark?: boolean }) {
  const user = useSession((s) => s.boot?.user);
  const navigate = useNavigate();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownTrigger asChild>
        <button className={cn('flex items-center gap-2 rounded-md px-1.5 py-1 text-left', dark ? 'hover:bg-white/10' : 'hover:bg-black/5')}>
          <span className="flex size-8 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white">{initials(user.fullName)}</span>
          <span className="hidden leading-tight sm:block">
            <span className={cn('block text-[13px] font-medium', dark && 'text-white')}>{user.fullName}</span>
            <span className={cn('block text-[11px]', dark ? 'text-slate-400' : 'text-fg-subtle')}>{user.roles.map((r) => r.name).join(', ') || 'No role'}</span>
          </span>
          <ChevronDown className={cn('size-4', dark ? 'text-slate-400' : 'text-fg-subtle')} />
        </button>
      </DropdownTrigger>
      <DropdownContent>
        <DropdownLabel>
          Signed in as <span className="font-medium text-fg">{user.username}</span>
          {user.branch && <span className="block">{user.branch.name}</span>}
        </DropdownLabel>
        <DropdownSeparator />
        <DropdownItem onSelect={() => void navigate({ to: '/' })}>
          <LayoutGrid /> All modules
        </DropdownItem>
        <DropdownItem onSelect={() => void navigate({ to: '/change-password' })}>
          <KeyRound /> Password & sign-in
        </DropdownItem>
        <DropdownSeparator />
        <ExperiencePanel />
        <DropdownSeparator />
        <DropdownItem danger onSelect={() => void logout().then(() => navigate({ to: '/login' }))}>
          <LogOut /> Sign out
        </DropdownItem>
      </DropdownContent>
    </DropdownMenu>
  );
}

/** Look & feel for this person: theme, how much motion, and UI sounds. */
function ExperiencePanel() {
  const { theme, motion, sound: on, volume, set } = useExperience();
  const seg = <T extends string>(value: T, options: [T, React.ReactNode, string][], onPick: (v: T) => void) => (
    <div className="flex rounded-md border border-border bg-surface-2 p-0.5" role="radiogroup">
      {options.map(([v, icon, label]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} title={label} onClick={(e) => { e.preventDefault(); onPick(v); }} className={cn('flex flex-1 items-center justify-center gap-1 rounded px-2 py-1 text-[11.5px] font-medium transition-colors [&_svg]:size-3.5', value === v ? 'bg-surface text-primary shadow-sm' : 'text-fg-muted hover:text-fg')}>
          {icon}{label}
        </button>
      ))}
    </div>
  );
  return (
    <div className="flex w-64 flex-col gap-2 px-2 py-1.5" onKeyDown={(e) => e.stopPropagation()}>
      <p className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Appearance</p>
      {seg<ThemeChoice>(theme, [['light', <Sun key="s" />, 'Light'], ['dark', <Moon key="m" />, 'Dark'], ['system', <Monitor key="c" />, 'Auto']], (v) => set({ theme: v }))}
      <p className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Motion</p>
      {seg<MotionChoice>(motion, [['full', null, 'Full'], ['lite', null, 'Lite'], ['off', null, 'Off']], (v) => set({ motion: v }))}
      <button type="button" onClick={(e) => { e.preventDefault(); set({ sound: !on }); if (!on) setTimeout(() => sound.success(), 30); }} className="flex items-center justify-between rounded-md px-1 py-1 text-[13px] hover:bg-surface-2">
        <span className="flex items-center gap-2 [&_svg]:size-4">{on ? <Volume2 /> : <VolumeX />} Sounds</span>
        <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', on ? 'bg-primary-soft text-primary' : 'bg-surface-2 text-fg-subtle')}>{on ? 'On' : 'Off'}</span>
      </button>
      {on && (
        <label className="flex items-center gap-2 px-1 pb-1 text-[11.5px] text-fg-muted">
          <span className="w-12 shrink-0">Volume</span>
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            value={volume ?? 0.6}
            aria-label="Sound volume"
            className="h-1.5 w-full cursor-pointer accent-(--color-primary)"
            onChange={(e) => set({ volume: Number(e.target.value) })}
            onPointerUp={() => sound.success()}
            onKeyUp={() => sound.tick()}
          />
          <span className="w-8 text-right tabular-nums">{Math.round((volume ?? 0.6) * 100)}%</span>
        </label>
      )}
    </div>
  );
}
